import { useEffect, useState } from 'react';
import { getFunnelSessionCode } from '../../../utils/session';
import { logFunnelEvent } from '../../../utils/funnelEvents';
import { trackPixelEvent, trackPixelCustomEvent } from '../../../utils/metaPixel';

interface StepFormProps {
  onNext: (data: any) => void;
  canProceed: boolean;
}

export default function StepForm({ onNext, canProceed }: StepFormProps) {
  const [aciklama, setAciklama] = useState('');
  const [telefon, setTelefon] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    logFunnelEvent({
      event_name: 'step1_form_view',
      step_no: 1
    });

    trackPixelCustomEvent('Step1FormView', {
      funnel_step: 1,
      importance: 5,
      content_name: 'İlk Form Görüntülendi'
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canProceed) return;

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/funnel_kaydet.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'step1',
          oturum_kodu: getFunnelSessionCode(),
          aciklama: aciklama,
          telefon: telefon
        })
      });

      const result = await response.json();

      if (result.status === 'success') {
        logFunnelEvent({
          event_name: 'step1_form_submitted',
          step_no: 1
        });

        trackPixelEvent('Lead', {
          funnel_step: 1,
          importance: 6,
          content_name: 'İlk Form Gönderildi'
        });

        trackPixelCustomEvent('Step1FormSubmitted', {
          funnel_step: 1,
          importance: 6,
          content_name: 'İlk Form Başarıyla Gönderildi'
        });

        onNext({ aciklama, telefon });
      } else {
        alert('Bilgiler kaydedilemedi. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Step 1 kayıt hatası:', error);
      alert('Sunucu bağlantı hatası oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black"></div>

      <div className="relative z-10 max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="flex -space-x-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 border-2 border-black flex items-center justify-center text-xs font-bold text-black">M</div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 border-2 border-black flex items-center justify-center text-xs font-bold text-white">A</div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 border-2 border-black flex items-center justify-center text-xs font-bold text-white">K</div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 border-2 border-black flex items-center justify-center text-xs font-bold text-black">S</div>
          </div>
          <p className="text-gray-300 text-sm font-medium">
            <span className="text-yellow-400 font-bold">200'den fazla</span> işletme bu sistemi test etti ve müşteri dönüşlerini artırdı.
          </p>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-2xl p-8 sm:p-12 shadow-2xl shadow-yellow-500/10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              İşletme Bilgileriniz
            </h2>
            <p className="text-yellow-400/90 text-base font-medium">
              Lütfen sesli mesajda sizden istenen bilgileri alt kısma giriniz.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" id="step1-form">

            {/* TELEFON (opsiyonel) */}
            <div>
              <label className="block text-white font-medium mb-2">
                Telefon (isteğe bağlı)
              </label>
              <input
                type="tel"
                name="telefon"
                value={telefon}
                onChange={e => setTelefon(e.target.value)}
                disabled={!canProceed || isSubmitting}
                className={`w-full px-4 py-3 bg-black/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-300 ${
                  canProceed
                    ? 'border-white/20 focus:border-yellow-500 focus:ring-yellow-500/20'
                    : 'border-white/10 opacity-50 cursor-not-allowed'
                }`}
                placeholder={canProceed ? '0555 555 55 55 (isteğe bağlı)' : 'Önce sesli mesajı dinleyin'}
              />
            </div>

            {/* AÇIKLAMA */}
            <div>
              <textarea
                name="aciklama"
                value={aciklama}
                onChange={e => setAciklama(e.target.value)}
                required
                rows={7}
                maxLength={500}
                disabled={!canProceed || isSubmitting}
                className={`w-full px-4 py-4 bg-black/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-300 resize-none text-sm ${
                  canProceed
                    ? 'border-white/20 focus:border-yellow-500 focus:ring-yellow-500/20'
                    : 'border-white/10 opacity-50 cursor-not-allowed'
                }`}
                placeholder={canProceed ? 'Bilgilerinizi buraya yazınız...' : 'Önce sesli mesajı dinleyip onaylayın...'}
              />
              <p className="text-right text-gray-500 text-xs mt-1">{aciklama.length}/500</p>
            </div>

            <button
              type="submit"
              disabled={!canProceed || isSubmitting}
              className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-300 whitespace-nowrap ${
                canProceed && !isSubmitting
                  ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black hover:shadow-2xl hover:shadow-yellow-500/50 hover:scale-105 cursor-pointer'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Gönderiliyor...' : (canProceed ? 'Bilgileri Gönder ve Sunum Videosuna Geç' : 'Önce Sesli Mesajı Dinleyin')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}